import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  writeBatch,
  Timestamp,
  deleteField
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { BaseService } from '../../Services/AdminService/BaseService';
import { createSuccessResponse, createErrorResponse } from './utils';
import { COLLECTIONS } from './types';

/**
 * ReportService - Handles all report and recommendation operations
 * Extends BaseService for common CRUD operations
 */
export class ReportService extends BaseService {
  constructor() {
    super(COLLECTIONS.REPORTS);
    
    // Initialize sub-collections references
    this.recommendationsCollection = collection(db, COLLECTIONS.RECOMMENDATIONS);
    this.pricingPlansCollection = collection(db, COLLECTIONS.PRICING_PLANS);
  }

  // ===== REPORT CRUD OPERATIONS =====

  /**
   * Create a new report with initial recommendation
   * @param {Object} reportData - Report data
   * @param {Object} initialRecommendation - First recommendation data
   * @param {string} adminUserId - Admin creating the report
   * @returns {Promise<Object>} Success/error response with report data
   */
  async createReport(reportData, initialRecommendation, adminUserId = 'admin') {
    try {
      // Validate report data
      const validationResult = this.validateReportData(reportData);
      if (!validationResult.isValid) {
        return createErrorResponse(validationResult.errors.join(', '), 'VALIDATION_ERROR');
      }

      // Validate initial recommendation
      const recommendationValidation = this.validateRecommendationData(initialRecommendation);
      if (!recommendationValidation.isValid) {
        return createErrorResponse(recommendationValidation.errors.join(', '), 'RECOMMENDATION_VALIDATION_ERROR');
      }

      // Create report structure
      const report = {
        ...reportData,
        planFeatures: this.generatePlanFeatures(reportData.allowedPlans),
        recommendations: [{
          ...initialRecommendation,
          recommendationId: this.generateRecommendationId(reportData.symbol),
          sequence: 1,
          status: 'ACTIVE',
          recommendationDate: Timestamp.now(),
          expiryDate: this.calculateExpiryDate(initialRecommendation.validityPeriod),
          createdBy: adminUserId,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        }],
        reportStatus: 'ACTIVE',
        overallPerformance: {
          totalReturn: 0,
          bestRecommendation: null,
          worstRecommendation: null,
          averageHoldingPeriod: 0,
          successRate: 0
        },
        summary: {
          initialRecommendation: null, // Will be set after creation
          latestRecommendation: null,  // Will be set after creation
          totalRecommendations: 1,
          activeRecommendations: 1,
          closedRecommendations: 0
        },
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: adminUserId,
        lastModifiedBy: adminUserId
      };

      // Add report to database
      const docRef = await addDoc(this.collectionRef, report);
      const createdReport = { id: docRef.id, ...report };

      // Update summary with recommendation IDs
      const updatedReport = await this.updateReportSummary(docRef.id, createdReport);

      return createSuccessResponse(updatedReport, 'Report created successfully');
    } catch (error) {
      return createErrorResponse('Failed to create report', 'CREATE_ERROR', error);
    }
  }

  /**
   * Get all reports with optional filters
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Success/error response with reports
   */
  async getAllReports(options = {}) {
    try {
      const reports = await this.getAll(options);
      return createSuccessResponse(reports, 'Reports fetched successfully');
    } catch (error) {
      return createErrorResponse('Failed to fetch reports', 'FETCH_ERROR', error);
    }
  }

  /**
   * Get report by ID with full details
   * @param {string} reportId - Report ID
   * @returns {Promise<Object>} Success/error response with report
   */
  async getReportById(reportId) {
    try {
      const report = await this.getById(reportId);
      if (!report) {
        return createErrorResponse('Report not found', 'NOT_FOUND');
      }
      return createSuccessResponse(report, 'Report fetched successfully');
    } catch (error) {
      return createErrorResponse('Failed to fetch report', 'FETCH_ERROR', error);
    }
  }

  /**
   * Update existing report
   * @param {string} reportId - Report ID
   * @param {Object} updateData - Data to update
   * @param {string} adminUserId - Admin updating the report
   * @returns {Promise<Object>} Success/error response with updated report
   */
  async updateReport(reportId, updateData, adminUserId = 'admin') {
    try {
      // Validate update data
      const validationResult = this.validateReportUpdateData(updateData);
      if (!validationResult.isValid) {
        return createErrorResponse(validationResult.errors.join(', '), 'VALIDATION_ERROR');
      }

      const updatedReport = await this.update(reportId, {
        ...updateData,
        updatedAt: Timestamp.now(),
        lastModifiedBy: adminUserId
      }, adminUserId);

      return createSuccessResponse(updatedReport, 'Report updated successfully');
    } catch (error) {
      return createErrorResponse('Failed to update report', 'UPDATE_ERROR', error);
    }
  }

  /**
   * Delete report and all its recommendations
   * @param {string} reportId - Report ID
   * @param {string} adminUserId - Admin deleting the report
   * @returns {Promise<Object>} Success/error response
   */
  async deleteReport(reportId, adminUserId = 'admin') {
    try {
      // Get report to find all recommendations
      const report = await this.getById(reportId);
      if (!report) {
        return createErrorResponse('Report not found', 'NOT_FOUND');
      }

      // Delete all recommendations first
      if (report.recommendations && report.recommendations.length > 0) {
        const batch = writeBatch(db);
        report.recommendations.forEach(rec => {
          if (rec.recommendationId) {
            const recRef = doc(this.recommendationsCollection, rec.recommendationId);
            batch.delete(recRef);
          }
        });
        await batch.commit();
      }

      // Delete the report
      await this.delete(reportId);

      return createSuccessResponse(null, 'Report deleted successfully');
    } catch (error) {
      return createErrorResponse('Failed to delete report', 'DELETE_ERROR', error);
    }
  }

  // ===== RECOMMENDATION MANAGEMENT =====

  /**
   * Add new recommendation to existing report
   * @param {string} reportId - Report ID
   * @param {Object} recommendationData - Recommendation data
   * @param {string} adminUserId - Admin adding the recommendation
   * @returns {Promise<Object>} Success/error response with updated report
   */
  async addRecommendation(reportId, recommendationData, adminUserId = 'admin') {
    try {
      // Validate recommendation data
      const validationResult = this.validateRecommendationData(recommendationData);
      if (!validationResult.isValid) {
        return createErrorResponse(validationResult.errors.join(', '), 'VALIDATION_ERROR');
      }

      // Get current report
      const report = await this.getById(reportId);
      if (!report) {
        return createErrorResponse('Report not found', 'NOT_FOUND');
      }

      // Generate new recommendation ID and sequence
      const newRecommendation = {
        ...recommendationData,
        recommendationId: this.generateRecommendationId(report.symbol, report.recommendations.length + 1),
        sequence: report.recommendations.length + 1,
        status: 'ACTIVE',
        recommendationDate: Timestamp.now(),
        expiryDate: this.calculateExpiryDate(recommendationData.validityPeriod),
        createdBy: adminUserId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // Add recommendation to report
      const updatedRecommendations = [...report.recommendations, newRecommendation];
      
      // Update report with new recommendation
      const updatedReport = await this.update(reportId, {
        recommendations: updatedRecommendations,
        summary: {
          ...report.summary,
          latestRecommendation: newRecommendation.recommendationId,
          totalRecommendations: updatedRecommendations.length,
          activeRecommendations: updatedRecommendations.filter(r => r.status === 'ACTIVE').length
        },
        updatedAt: Timestamp.now(),
        lastModifiedBy: adminUserId
      }, adminUserId);

      return createSuccessResponse(updatedReport, 'Recommendation added successfully');
    } catch (error) {
      return createErrorResponse('Failed to add recommendation', 'ADD_RECOMMENDATION_ERROR', error);
    }
  }

  /**
   * Update existing recommendation
   * @param {string} reportId - Report ID
   * @param {string} recommendationId - Recommendation ID
   * @param {Object} updateData - Data to update
   * @param {string} adminUserId - Admin updating the recommendation
   * @returns {Promise<Object>} Success/error response with updated report
   */
  async updateRecommendation(reportId, recommendationId, updateData, adminUserId = 'admin') {
    try {
      // Get current report
      const report = await this.getById(reportId);
      if (!report) {
        return createErrorResponse('Report not found', 'NOT_FOUND');
      }

      // Find and update recommendation
      const recommendationIndex = report.recommendations.findIndex(r => r.recommendationId === recommendationId);
      if (recommendationIndex === -1) {
        return createErrorResponse('Recommendation not found', 'NOT_FOUND');
      }

      // Update recommendation
      const updatedRecommendations = [...report.recommendations];
      updatedRecommendations[recommendationIndex] = {
        ...updatedRecommendations[recommendationIndex],
        ...updateData,
        updatedAt: Timestamp.now()
      };

      // Update report
      const updatedReport = await this.update(reportId, {
        recommendations: updatedRecommendations,
        updatedAt: Timestamp.now(),
        lastModifiedBy: adminUserId
      }, adminUserId);

      return createSuccessResponse(updatedReport, 'Recommendation updated successfully');
    } catch (error) {
      return createErrorResponse('Failed to update recommendation', 'UPDATE_RECOMMENDATION_ERROR', error);
    }
  }

  /**
   * Delete recommendation from report
   * @param {string} reportId - Report ID
   * @param {string} recommendationId - Recommendation ID
   * @param {string} adminUserId - Admin deleting the recommendation
   * @returns {Promise<Object>} Success/error response with updated report
   */
  async deleteRecommendation(reportId, recommendationId, adminUserId = 'admin') {
    try {
      // Get current report
      const report = await this.getById(reportId);
      if (!report) {
        return createErrorResponse('Report not found', 'NOT_FOUND');
      }

      // Remove recommendation
      const updatedRecommendations = report.recommendations.filter(r => r.recommendationId !== recommendationId);
      
      // Reorder remaining recommendations
      updatedRecommendations.forEach((rec, index) => {
        rec.sequence = index + 1;
      });

      // Update report
      const updatedReport = await this.update(reportId, {
        recommendations: updatedRecommendations,
        summary: {
          ...report.summary,
          totalRecommendations: updatedRecommendations.length,
          activeRecommendations: updatedRecommendations.filter(r => r.status === 'ACTIVE').length,
          closedRecommendations: updatedRecommendations.filter(r => r.status !== 'ACTIVE').length
        },
        updatedAt: Timestamp.now(),
        lastModifiedBy: adminUserId
      }, adminUserId);

      return createSuccessResponse(updatedReport, 'Recommendation deleted successfully');
    } catch (error) {
      return createErrorResponse('Failed to delete recommendation', 'DELETE_RECOMMENDATION_ERROR', error);
    }
  }

  // ===== REPORT QUERIES & FILTERS =====

  /**
   * Get reports by symbol
   * @param {string} symbol - Stock symbol
   * @returns {Promise<Object>} Success/error response with reports
   */
  async getReportsBySymbol(symbol) {
    try {
      const reports = await this.getAll({
        filters: [{ field: 'symbol', operator: '==', value: symbol.toUpperCase() }]
      });
      return createSuccessResponse(reports, 'Reports fetched successfully');
    } catch (error) {
      return createErrorResponse('Failed to fetch reports by symbol', 'FETCH_ERROR', error);
    }
  }

  /**
   * Get reports by sector
   * @param {string} sector - Sector name
   * @returns {Promise<Object>} Success/error response with reports
   */
  async getReportsBySector(sector) {
    try {
      const reports = await this.getAll({
        filters: [{ field: 'sector', operator: '==', value: sector }]
      });
      return createSuccessResponse(reports, 'Reports fetched successfully');
    } catch (error) {
      return createErrorResponse('Failed to fetch reports by sector', 'FETCH_ERROR', error);
    }
  }

  /**
   * Get reports by plan access
   * @param {string} planName - Plan name
   * @returns {Promise<Object>} Success/error response with reports
   */
  async getReportsByPlan(planName) {
    try {
      const reports = await this.getAll({
        filters: [{ field: 'allowedPlans', operator: 'array-contains', value: planName }]
      });
      return createSuccessResponse(reports, 'Reports fetched successfully');
    } catch (error) {
      return createErrorResponse('Failed to fetch reports by plan', 'FETCH_ERROR', error);
    }
  }

  /**
   * Get active reports
   * @returns {Promise<Object>} Success/error response with active reports
   */
  async getActiveReports() {
    try {
      const reports = await this.getAll({
        filters: [{ field: 'reportStatus', operator: '==', value: 'ACTIVE' }]
      });
      return createSuccessResponse(reports, 'Active reports fetched successfully');
    } catch (error) {
      return createErrorResponse('Failed to fetch active reports', 'FETCH_ERROR', error);
    }
  }

  /**
   * Search reports by title or company name
   * @param {string} searchTerm - Search term
   * @returns {Promise<Object>} Success/error response with matching reports
   */
  async searchReports(searchTerm) {
    try {
      // Get all reports and filter by search term
      const allReports = await this.getAll();
      const matchingReports = allReports.filter(report => 
        report.reportTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.symbol?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      return createSuccessResponse(matchingReports, 'Search results fetched successfully');
    } catch (error) {
      return createErrorResponse('Failed to search reports', 'SEARCH_ERROR', error);
    }
  }

  // ===== PERFORMANCE & ANALYTICS =====

  /**
   * Calculate report performance metrics
   * @param {string} reportId - Report ID
   * @returns {Promise<Object>} Success/error response with performance data
   */
  async calculateReportPerformance(reportId) {
    try {
      const report = await this.getById(reportId);
      if (!report) {
        return createErrorResponse('Report not found', 'NOT_FOUND');
      }

      const performance = this.calculatePerformanceMetrics(report.recommendations);
      
      // Update report with new performance data
      await this.update(reportId, {
        overallPerformance: performance,
        updatedAt: Timestamp.now()
      });

      return createSuccessResponse(performance, 'Performance calculated successfully');
    } catch (error) {
      return createErrorResponse('Failed to calculate performance', 'CALCULATION_ERROR', error);
    }
  }

  /**
   * Get recommendation history for a report
   * @param {string} reportId - Report ID
   * @param {number} limit - Maximum number of recommendations to return
   * @returns {Promise<Object>} Success/error response with recommendations
   */
  async getRecommendationHistory(reportId, limit = 10) {
    try {
      const report = await this.getById(reportId);
      if (!report) {
        return createErrorResponse('Report not found', 'NOT_FOUND');
      }

      const recommendations = report.recommendations
        .sort((a, b) => b.recommendationDate.toDate() - a.recommendationDate.toDate())
        .slice(0, limit);

      return createSuccessResponse(recommendations, 'Recommendation history fetched successfully');
    } catch (error) {
      return createErrorResponse('Failed to fetch recommendation history', 'FETCH_ERROR', error);
    }
  }

  // ===== UTILITY METHODS =====

  /**
   * Generate unique recommendation ID
   * @param {string} symbol - Stock symbol
   * @param {number} sequence - Sequence number
   * @returns {string} Unique recommendation ID
   */
  generateRecommendationId(symbol, sequence = 1) {
    const timestamp = Date.now().toString(36);
    return `${symbol.toUpperCase()}-${sequence.toString().padStart(3, '0')}-${timestamp}`;
  }

  /**
   * Calculate expiry date based on validity period
   * @param {number} validityPeriod - Validity period in days
   * @returns {Timestamp} Expiry timestamp
   */
  calculateExpiryDate(validityPeriod) {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + validityPeriod);
    return Timestamp.fromDate(expiryDate);
  }

  /**
   * Generate plan features object for report
   * @param {Array} allowedPlans - Array of allowed plan names
   * @returns {Object} Plan features configuration
   */
  generatePlanFeatures(allowedPlans) {
    const allPlans = ['Prime', 'Prime+', 'Elite', 'Elite+', 'Supreme', 'Supreme+'];
    
    return allPlans.reduce((features, plan) => {
      features[plan] = {
        canAccess: allowedPlans.includes(plan),
        maxStocks: allowedPlans.includes(plan) ? this.getPlanMaxStocks(plan) : 0,
        restrictions: []
      };
      return features;
    }, {});
  }

  /**
   * Get maximum stocks for a plan
   * @param {string} planName - Plan name
   * @returns {number} Maximum stocks allowed
   */
  getPlanMaxStocks(planName) {
    const planLimits = {
      'Prime': 1,
      'Prime+': 1,
      'Elite': 2,
      'Elite+': 2,
      'Supreme': 3,
      'Supreme+': 3
    };
    return planLimits[planName] || 0;
  }

  /**
   * Update report summary after changes
   * @param {string} reportId - Report ID
   * @param {Object} report - Report data
   * @returns {Promise<Object>} Updated report
   */
  async updateReportSummary(reportId, report) {
    const summary = {
      initialRecommendation: report.recommendations[0]?.recommendationId || null,
      latestRecommendation: report.recommendations[report.recommendations.length - 1]?.recommendationId || null,
      totalRecommendations: report.recommendations.length,
      activeRecommendations: report.recommendations.filter(r => r.status === 'ACTIVE').length,
      closedRecommendations: report.recommendations.filter(r => r.status !== 'ACTIVE').length
    };

    return await this.update(reportId, { summary }, 'system');
  }

  /**
   * Calculate performance metrics from recommendations
   * @param {Array} recommendations - Array of recommendations
   * @returns {Object} Performance metrics
   */
  calculatePerformanceMetrics(recommendations) {
    if (!recommendations || recommendations.length === 0) {
      return {
        totalReturn: 0,
        successRate: 0,
        averageHoldingPeriod: 0,
        totalRecommendations: 0,
        activeRecommendations: 0,
        closedRecommendations: 0
      };
    }

    const closedRecommendations = recommendations.filter(rec => rec.status !== 'ACTIVE');
    const activeRecommendations = recommendations.filter(rec => rec.status === 'ACTIVE');
    
    const successfulRecommendations = closedRecommendations.filter(rec => rec.targetMet === true);
    const totalReturn = closedRecommendations.reduce((sum, rec) => sum + (rec.actualReturn || 0), 0);
    
    const averageHoldingPeriod = closedRecommendations.length > 0 
      ? closedRecommendations.reduce((sum, rec) => sum + (rec.holdingPeriod || 0), 0) / closedRecommendations.length
      : 0;

    return {
      totalReturn,
      successRate: closedRecommendations.length > 0 
        ? (successfulRecommendations.length / closedRecommendations.length) * 100 
        : 0,
      averageHoldingPeriod,
      totalRecommendations: recommendations.length,
      activeRecommendations: activeRecommendations.length,
      closedRecommendations: closedRecommendations.length
    };
  }

  // ===== VALIDATION METHODS =====

  /**
   * Validate report data
   * @param {Object} reportData - Report data to validate
   * @returns {Object} Validation result
   */
  validateReportData(reportData) {
    const errors = [];
    const required = ['symbol', 'companyName', 'sector', 'reportTitle', 'allowedPlans'];

    required.forEach(field => {
      if (!reportData[field]) {
        errors.push(`${field} is required`);
      }
    });

    if (reportData.allowedPlans && !Array.isArray(reportData.allowedPlans)) {
      errors.push('allowedPlans must be an array');
    }

    if (reportData.minRiskLevel && (reportData.minRiskLevel < 1 || reportData.minRiskLevel > 5)) {
      errors.push('minRiskLevel must be between 1 and 5');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate recommendation data
   * @param {Object} recommendationData - Recommendation data to validate
   * @returns {Object} Validation result
   */
  validateRecommendationData(recommendationData) {
    const errors = [];
    const required = ['action', 'recommendationType', 'currentPrice', 'targetPrice', 'stopLoss', 'validityPeriod'];

    required.forEach(field => {
      if (recommendationData[field] === undefined || recommendationData[field] === null) {
        errors.push(`${field} is required`);
      }
    });

    if (recommendationData.currentPrice <= 0) {
      errors.push('currentPrice must be positive');
    }

    if (recommendationData.targetPrice <= 0) {
      errors.push('targetPrice must be positive');
    }

    if (recommendationData.stopLoss <= 0) {
      errors.push('stopLoss must be positive');
    }

    if (recommendationData.validityPeriod <= 0) {
      errors.push('validityPeriod must be positive');
    }

    const validActions = ['BUY', 'SELL', 'HOLD', 'AVERAGE', 'EXIT_PARTIAL', 'EXIT_FULL', 'PYRAMID'];
    if (!validActions.includes(recommendationData.action)) {
      errors.push(`action must be one of: ${validActions.join(', ')}`);
    }

    const validTypes = ['INITIAL', 'FOLLOW_UP', 'EXIT', 'AVERAGE_DOWN', 'AVERAGE_UP'];
    if (!validTypes.includes(recommendationData.recommendationType)) {
      errors.push(`recommendationType must be one of: ${validTypes.join(', ')}`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate report update data
   * @param {Object} updateData - Update data to validate
   * @returns {Object} Validation result
   */
  validateReportUpdateData(updateData) {
    const errors = [];

    if (updateData.allowedPlans && !Array.isArray(updateData.allowedPlans)) {
      errors.push('allowedPlans must be an array');
    }

    if (updateData.minRiskLevel && (updateData.minRiskLevel < 1 || updateData.minRiskLevel > 5)) {
      errors.push('minRiskLevel must be between 1 and 5');
    }

    if (updateData.reportStatus && !['ACTIVE', 'CLOSED', 'ARCHIVED'].includes(updateData.reportStatus)) {
      errors.push('reportStatus must be ACTIVE, CLOSED, or ARCHIVED');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
} 